/**
 * Playtest hosting types and credit estimate (matches immersely.ai/pricing).
 * Formula: Players × Hours × 15 credits (or total hours × 15 when locked).
 */

export const CREDITS_PER_PLAY_HOUR = 15;
export const MIN_PLAYTESTERS = 1;
export const MAX_PLAYTESTERS = 100;
export const FREE_PRIVATE_MAX_PLAYTESTERS = 10;
export const MIN_SESSION_HOURS = 0.5;
export const MAX_SESSION_HOURS = 8;
export const SESSION_HOURS_STEP = 0.5;
export const PRICING_URL = "https://www.immersely.ai/pricing";

export const PLAYTEST_TYPES = [
  {
    id: "free_public",
    name: "Free public playtest",
    priceLabel: "Free",
    badge: "Public",
    summary: "Open to every playtester. They choose when to play and earn credits for taking part.",
    assignment: "opt-in",
    canAssignPlaytesters: false,
    canSetPlaytime: false,
    minPlaytesters: 0,
    maxPlaytesters: 0,
    defaultPlaytesters: 0,
    outputs: ["Basic report"],
  },
  {
    id: "free_private",
    name: "Free private playtest",
    priceLabel: "Free",
    badge: "Private",
    summary: "Invite up to 10 playtesters to a private session. Analysis is limited to emotional signals.",
    assignment: "assign",
    canAssignPlaytesters: true,
    canSetPlaytime: false,
    minPlaytesters: 1,
    maxPlaytesters: FREE_PRIVATE_MAX_PLAYTESTERS,
    defaultPlaytesters: 5,
    outputs: ["Limited emotional signal"],
  },
  {
    id: "paid_private",
    name: "Paid private playtest",
    priceLabel: "Paid",
    badge: "Full stack",
    summary: "Assigned private playtest with the full analysis stack. Credit cost is an estimate only.",
    assignment: "assign",
    canAssignPlaytesters: true,
    canSetPlaytime: true,
    minPlaytesters: 1,
    maxPlaytesters: MAX_PLAYTESTERS,
    defaultPlaytesters: 12,
    outputs: [
      "Emotion benchmarks",
      "Clip generation",
      "Behavioral mapping",
      "Genre benchmarking",
    ],
  },
];

export function getPlaytestType(id) {
  return PLAYTEST_TYPES.find((type) => type.id === id) || PLAYTEST_TYPES[0];
}

export function clampPlaytesterCount(count, playtestType) {
  const type = getPlaytestType(playtestType);
  if (!type.canAssignPlaytesters) return 0;
  const num = Number(count);
  if (!Number.isFinite(num)) return type.defaultPlaytesters;
  return Math.min(type.maxPlaytesters, Math.max(type.minPlaytesters, Math.round(num)));
}

export function clampSessionHours(hours) {
  const num = Number(hours);
  if (!Number.isFinite(num)) return 1;
  const stepped = Math.round(num / SESSION_HOURS_STEP) * SESSION_HOURS_STEP;
  return Math.min(MAX_SESSION_HOURS, Math.max(MIN_SESSION_HOURS, Number(stepped.toFixed(1))));
}

/**
 * @returns {{ creditsRequired: number, totalHours: number, hoursPerPlayer: number }}
 */
export function estimatePlaytestCredits({
  playtestType,
  numPlaytesters,
  sessionHours,
  lockTotalPlaytime,
}) {
  const type = getPlaytestType(playtestType);
  const players = clampPlaytesterCount(numPlaytesters, playtestType);
  const hours = type.canSetPlaytime ? clampSessionHours(sessionHours) : 0;
  const totalHours = !type.canSetPlaytime
    ? 0
    : lockTotalPlaytime
      ? hours
      : players * hours;
  const hoursPerPlayer = !type.canSetPlaytime
    ? 0
    : lockTotalPlaytime && players > 0
      ? Number((hours / players).toFixed(2))
      : hours;
  const creditsRequired =
    playtestType === "paid_private"
      ? Math.ceil(totalHours * CREDITS_PER_PLAY_HOUR)
      : 0;

  return { creditsRequired, totalHours, hoursPerPlayer, players, hours };
}

export function formatHoursLabel(hours) {
  const value = Number(hours);
  if (!Number.isFinite(value)) return "—";
  if (value === 1) return "1 hour";
  return `${value} hours`;
}

/**
 * Assigned playtime as configured on the game.
 * Paid private: sessionHours is per player, or total hours when lockTotalPlaytime is true.
 */
export function getGamePlaytime(game) {
  const typeId = resolvePlaytestTypeId(game);
  const type = getPlaytestType(typeId);
  const players = Number(game?.numPlaytesters);
  const hours = Number(game?.sessionHours);
  const locked = !!game?.lockTotalPlaytime;

  if (typeId === "free_public" || typeId === "free_private" || !type?.canSetPlaytime) {
    if (!Number.isFinite(hours) || hours <= 0) {
      return {
        configured: false,
        hoursPerPlayer: null,
        totalHours: null,
        lockTotalPlaytime: locked,
        perPlayerLabel: typeId === "free_private" ? "Not configured" : "—",
        summaryLabel: typeId === "free_private" ? "Playtime not configured" : "Playtime not set",
      };
    }
  }

  if (!Number.isFinite(hours) || hours <= 0) {
    return {
      configured: false,
      hoursPerPlayer: null,
      totalHours: null,
      lockTotalPlaytime: locked,
      perPlayerLabel: "—",
      summaryLabel: "Playtime not set",
    };
  }

  const totalHours = locked ? hours : (Number.isFinite(players) && players > 0 ? players * hours : hours);
  const hoursPerPlayer =
    locked && Number.isFinite(players) && players > 0
      ? Number((hours / players).toFixed(2))
      : hours;

  return {
    configured: true,
    hoursPerPlayer,
    totalHours,
    lockTotalPlaytime: locked,
    perPlayerLabel: formatHoursLabel(hoursPerPlayer),
    summaryLabel: locked
      ? `${formatHoursLabel(totalHours)} total · ${formatHoursLabel(hoursPerPlayer)} each`
      : `${formatHoursLabel(hoursPerPlayer)} per playtester`,
  };
}

export function getPlaytestTypeLabel(game) {
  if (game?.playtestType) {
    return getPlaytestType(game.playtestType).name;
  }
  if (game?.isPublicPlaytest) return "Free public playtest";
  return null;
}

export function resolvePlaytestTypeId(game) {
  if (game?.playtestType) return game.playtestType;
  if (game?.isPublicPlaytest) return "free_public";
  return null;
}

export function isPublicPlaytestGame(game) {
  return resolvePlaytestTypeId(game) === "free_public" || !!game?.isPublicPlaytest;
}

export function isAdminAssignablePlaytest(game) {
  const typeId = resolvePlaytestTypeId(game);
  if (typeId === "free_public") return false;
  if (game?.isPublicPlaytest) return false;
  return typeId === "free_private" || typeId === "paid_private" || !typeId;
}
